/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Lock, ArrowRight, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InviteActivationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, valid, used, expired, invalid
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/invite/${token}`);
        setInvite(res.data);
        setStatus('valid');
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('already been used')) setStatus('used');
        else if (detail.includes('expired')) setStatus('expired');
        else setStatus('invalid');
      }
    };
    validate();
  }, [token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/invite/${token}/create-account`, { password }, { withCredentials: true });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center p-4" data-testid="invite-activation-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <Img variant="generic" src="/brand/logo-color.png" alt="DCCF" className="h-14 mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {status === 'loading' && (
            <div className="p-10 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-slate-600 text-sm">Validating your invitation...</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="p-10 text-center">
              <XCircle className="w-14 h-14 text-red-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
              <p className="text-sm text-slate-500 mb-6">This invitation link is not valid. Please contact the membership team.</p>
              <Link to="/"><Button variant="outline" className="rounded-full">Back to Website</Button></Link>
            </div>
          )}

          {status === 'used' && (
            <div className="p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Already Activated</h2>
              <p className="text-sm text-slate-500 mb-6">This invitation has already been used. You can sign in to your account.</p>
              <Link to="/member-login"><Button className="bg-[#E63946] hover:bg-[#d32f3f] rounded-full">Sign In</Button></Link>
            </div>
          )}

          {status === 'expired' && (
            <div className="p-10 text-center">
              <AlertTriangle className="w-14 h-14 text-amber-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation Expired</h2>
              <p className="text-sm text-slate-500 mb-6">This invitation has expired. Please contact the membership team for a new one.</p>
              <Link to="/contact"><Button variant="outline" className="rounded-full">Contact Us</Button></Link>
            </div>
          )}

          {status === 'valid' && !done && (
            <div>
              <div className="bg-gradient-to-r from-[#E63946] to-[#C62D3A] px-6 py-5 text-white text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-white/80" />
                <h2 className="text-xl font-bold">You've Been Approved!</h2>
                <p className="text-white/80 text-sm mt-1">Welcome to the DCCF, {invite?.name}</p>
              </div>
              <div className="p-6">
                <div className="bg-slate-50 rounded-xl p-4 mb-5 text-sm text-slate-600">
                  <p><strong>Name:</strong> {invite?.name}</p>
                  <p><strong>Email:</strong> {invite?.email}</p>
                  {invite?.company && <p><strong>Company:</strong> {invite?.company}</p>}
                </div>
                <h3 className="font-semibold text-slate-900 mb-3">Create your password to activate your account</h3>
                <form onSubmit={handleActivate} className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600">Password (min 8 characters)</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} className="pl-10 h-11 rounded-full" placeholder="Create a password" data-testid="invite-password" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Confirm Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="pl-10 h-11 rounded-full" placeholder="Confirm password" data-testid="invite-confirm-password" />
                    </div>
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] rounded-full text-sm font-semibold mt-2" data-testid="activate-account-btn">
                    {submitting ? 'Creating Account...' : 'Activate My Account'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </div>
            </div>
          )}

          {done && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Account Created!</h2>
              <p className="text-sm text-slate-500 mb-6">Your DCCF member account is ready. Access your portal now.</p>
              <Button onClick={() => navigate('/portal')} className="bg-[#E63946] hover:bg-[#d32f3f] rounded-full px-8" data-testid="go-to-portal-btn">
                Enter Member Portal <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InviteActivationPage;
